import React, { useEffect, useState } from "react";
import TeamDropdown from "../user/TeamDropdown";
import BranchDropdown from "../user/BranchDropdown";
import "./AdminApprovalDashboard.css";

const TimesheetSearch = ({
  initialConfig,
  onSearch,
  onReportGen,
  presetFilters,
}) => {
  const [filters, setFilters] = useState({});
  const [datesLocked, setDatesLocked] = useState(false);

  // Used when clicking the search icon beside a Job Number
  useEffect(() => {
    if (!presetFilters) return;

    setFilters((prev) => ({
      ...prev,
      ...presetFilters,
    }));
  }, [presetFilters]);

  const handleInputChange = (event) => {
    const { name, value, type, checked } = event.target;

    setFilters((prevFilters) => ({
      ...prevFilters,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleTeamChange = (selectedTeamId) => {
    setFilters((prev) => ({
      ...prev,
      teamId: selectedTeamId,
    }));
  };

  const handleBranchChange = (selectedBranchId) => {
    setFilters((prev) => ({
      ...prev,
      branchId: selectedBranchId,
    }));
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    onSearch(filters);
  };

  const handleReportGen = (event) => {
    event.preventDefault();
    onReportGen(filters);
  };

  const handleClearFilter = () => {
    let clearedFilters = {};

    // If dates are locked, Clear must retain them.
    if (datesLocked) {
      clearedFilters = {
        startdate: filters.startdate || "",
        enddate: filters.enddate || "",
      };
    }

    setFilters(clearedFilters);
    onSearch(clearedFilters);
  };

  const renderFilter = (filter) => {
    switch (filter.type) {
      /* =====================================================
         TEXT FIELDS
         ===================================================== */
      case "text":
        return (
          <div
            key={filter.name}
            className="approval-filter-field"
          >
            <label
              htmlFor={filter.name}
              className="approval-filter-label"
            >
              {filter.label}
            </label>

            <input
              type="text"
              id={filter.name}
              name={filter.name}
              value={filters[filter.name] || ""}
              onChange={handleInputChange}
            />
          </div>
        );

      /* =====================================================
         TEAM / BRANCH
         ===================================================== */
      case "select":
        return (
          <div
            key={filter.name}
            className="approval-filter-field"
          >
            <label
              htmlFor={filter.name}
              className="approval-filter-label"
            >
              {filter.label}
            </label>

            {filter.name === "Team" && (
              <div style={{ width: "100%" }}>
                <TeamDropdown
                  onSelectTeam={handleTeamChange}
                  defaultValue={filters.teamId}
                  height={36}
                />
              </div>
            )}

            {filter.name === "Branch" && (
              <div style={{ width: "100%" }}>
                <BranchDropdown
                  onSelectBranch={handleBranchChange}
                  defaultValue={filters.branchId}
                  settings={{
                    height: 36,
                    width: "100%",
                    notRequired: true,
                    fontSize: 12,
                  }}
                />
              </div>
            )}
          </div>
        );

      /* =====================================================
         ENGINEER / PROCESS DROPDOWN
         ===================================================== */
      case "customSelect":
        return (
          <div
            key={filter.name}
            className="approval-filter-field"
          >
            <label
              htmlFor={filter.name}
              className="approval-filter-label"
            >
              {filter.label}
            </label>

            <select
              id={filter.name}
              name={filter.name}
              value={filters[filter.name] || ""}
              onChange={handleInputChange}
            >
              <option value="">
                Select {filter.label}
              </option>

              {filter.options.map((option) => (
                <option
                  key={option.value}
                  value={option.value}
                >
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        );

      /* =====================================================
         START / END DATE
         ===================================================== */
      case "date":
        return (
          <div
            key={filter.name}
            className="approval-filter-field"
          >
            <div
              className={
                filter.name === "enddate"
                  ? "approval-filter-label approval-date-label"
                  : "approval-filter-label"
              }
            >
              <label
                htmlFor={filter.name}
                style={{
                  margin: 0,
                  color: "inherit",
                  fontSize: "inherit",
                  fontWeight: "inherit",
                  textTransform: "inherit",
                  letterSpacing: "inherit",
                }}
              >
                {filter.label}
              </label>

              {/* One lock protects BOTH Start and End from Clear */}
              {filter.name === "enddate" && (
                <button
                  type="button"
                  className={
                    datesLocked
                      ? "approval-date-lock locked"
                      : "approval-date-lock"
                  }
                  title={
                    datesLocked
                      ? "Dates Locked - Click to Unlock"
                      : "Lock Start and End Dates"
                  }
                  onClick={() =>
                    setDatesLocked((prev) => !prev)
                  }
                >
                  <i
                    className={
                      datesLocked
                        ? "fa fa-lock"
                        : "fa fa-unlock"
                    }
                  ></i>
                </button>
              )}
            </div>

            <input
              type="date"
              id={filter.name}
              name={filter.name}
              value={filters[filter.name] || ""}
              onChange={handleInputChange}
            />
          </div>
        );

      /* =====================================================
         OVERTIME
         ===================================================== */
      case "checkbox":
        return (
          <div
            key={filter.name}
            className="approval-filter-field approval-checkbox-field"
          >
            <span className="approval-filter-label">
              {filter.label}
            </span>

            <label
              htmlFor={filter.name}
              className="approval-checkbox-wrapper"
            >
              <input
                type="checkbox"
                id={filter.name}
                name={filter.name}
                checked={filters[filter.name] || false}
                onChange={handleInputChange}
              />

              Include Overtime
            </label>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      {/* SEARCH FIELDS */}
      <div className="approval-filter-grid">
        {initialConfig.map((filter) =>
          renderFilter(filter)
        )}
      </div>

      {/* SEARCH BUTTONS */}
      <div className="approval-filter-actions">
        <button
          type="submit"
          className="
            approval-action-button
            approval-search-button
          "
        >
          <i className="fas fa-search"></i>
          Search
        </button>

        <button
          type="button"
          className="
            approval-action-button
            approval-report-button
          "
          onClick={handleReportGen}
        >
          <i className="fa fa-file-excel-o"></i>
          Create Report
        </button>

        <button
          type="button"
          className="
            approval-action-button
            approval-clear-button
          "
          onClick={handleClearFilter}
        >
          <i
            className="fa fa-refresh"
            aria-hidden="true"
          ></i>
          Clear
        </button>
      </div>
    </form>
  );
};

export default TimesheetSearch;